import { AnalysisConfiguration } from '../types/index.js';

/**
 * Manages audio analysis and processing for the host side.
 * Handles audio stream processing and provides real-time audio analysis for Phase 5.
 * 
 * This system provides:
 * - Real-time audio analysis (RMS, peak, frequency bands)
 * - AudioContext management and cleanup
 * - Raw FFT data access for custom analysis
 * - Analysis results sent to worker for artist API
 * - Message handling for audio stream updates
 */
export class AudioSystem {
  // Audio context and analysis nodes
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private currentStream: MediaStream | null = null;
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }
  
  // Analysis configuration (good balance, leaning towards quality)
  private fftSize = 2048; // Good balance for quality vs performance
  private smoothingTimeConstant = 0.8; // Smooth but responsive
  
  // Analysis data arrays
  private frequencyData: Uint8Array<ArrayBuffer> | null = null;
  private timeDomainData: Uint8Array<ArrayBuffer> | null = null;
  
  // Audio analysis state (host-side state)
  private audioState = {
    isConnected: false,
    volume: {
      rms: 0,
      peak: 0
    },
    bands: {
      bass: 0,
      mid: 0,
      treble: 0,
      subBass: 0,
      lowMid: 0,
      highMid: 0,
      presence: 0,
      brilliance: 0
    }
  };
  
  // Analysis loop
  private analysisLoopId: number | null = null;
  private isAnalysisRunning = false;
  
  // Callback to send results to worker
  private sendAnalysisResults: ((data: any) => void) | null = null;

  constructor(sendAnalysisResultsCallback?: (data: any) => void) {
    // Bind methods for proper context
    this.handleAudioStreamUpdate = this.handleAudioStreamUpdate.bind(this);
    this.performAnalysis = this.performAnalysis.bind(this);
    this.sendAnalysisResults = sendAnalysisResultsCallback || null;
  }

  /**
   * Get the current audio analysis state (for host-side usage)
   */
  public getAudioState() {
    return { ...this.audioState };
  }

  /**
   * Handle audio stream update (called from VijiCore)
   */
  public handleAudioStreamUpdate(data: {
    audioStream?: MediaStream | null;
    analysisConfig?: AnalysisConfiguration;
    timestamp: number;
  }): void {
    try {
      if (data.audioStream) {
        this.setAudioStream(data.audioStream);
      } else {
        // Stream removed or null
        this.disconnectAudioStream();
      }
      
      // Update analysis configuration if provided
      if (data.analysisConfig) {
        this.updateAnalysisConfig(data.analysisConfig);
      }
    } catch (error) {
      console.error('Error handling audio stream update:', error);
      this.audioState.isConnected = false;
      this.sendAnalysisResultsToWorker();
    }
  }

  /**
   * Set the audio stream for analysis
   */
  private async setAudioStream(audioStream: MediaStream): Promise<void> {
    // Clean up existing connections
    this.disconnectAudioStream();
    
    // Validate stream has audio tracks
    const audioTracks = audioStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn('No audio tracks in provided stream');
      this.audioState.isConnected = false;
      this.sendAnalysisResultsToWorker();
      return;
    }
    
    try {
      // Create AudioContext if needed
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
        
        // Resume context if suspended (required by browser policies)
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }
      }
      
      // Create analyser node with our configuration
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.fftSize;
      this.analyser.smoothingTimeConstant = this.smoothingTimeConstant;
      
      // Create media stream source
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(audioStream);
      
      // Connect stream to analyser (no need to connect to destination for analysis)
      this.mediaStreamSource.connect(this.analyser);
      
      // Initialize data arrays
      const bufferLength = this.analyser.frequencyBinCount;
      this.frequencyData = new Uint8Array(bufferLength);
      this.timeDomainData = new Uint8Array(bufferLength);
      
      // Store stream reference
      this.currentStream = audioStream;
      this.audioState.isConnected = true;
      
      // Start analysis loop
      this.startAnalysisLoop();
      
      this.debugLog('Audio stream connected successfully (host-side)', {
        sampleRate: this.audioContext.sampleRate,
        fftSize: this.fftSize,
        bufferLength
      });
      
    } catch (error) {
      console.error('Failed to set up audio analysis:', error);
      this.audioState.isConnected = false;
      this.disconnectAudioStream();
    }
    
    // Send initial state to worker
    this.sendAnalysisResultsToWorker();
  }

  /**
   * Disconnect current audio stream and clean up resources
   */
  private disconnectAudioStream(): void {
    // Stop analysis loop
    this.stopAnalysisLoop();
    
    // Disconnect and clean up audio nodes
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
    
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
    
    // Reset data arrays
    this.frequencyData = null;
    this.timeDomainData = null;
    
    // Clear stream reference
    this.currentStream = null;
    
    // Update state
    this.audioState.isConnected = false;
    this.resetAudioValues();
    
    // Send updated state to worker
    this.sendAnalysisResultsToWorker();
    
    this.debugLog('Audio stream disconnected (host-side)');
  }

  /**
   * Update analysis configuration
   */
  private updateAnalysisConfig(config: AnalysisConfiguration): void {
    let needsReconnect = false;
    
    if (config.fftSize && config.fftSize !== this.fftSize) {
      this.fftSize = config.fftSize;
      needsReconnect = true;
    }
    
    if (config.smoothing !== undefined) {
      this.smoothingTimeConstant = config.smoothing;
      if (this.analyser) {
        this.analyser.smoothingTimeConstant = this.smoothingTimeConstant;
      }
    }
    
    // Reconnect if FFT size changed (requires new analyser)
    if (needsReconnect && this.currentStream) {
      const stream = this.currentStream;
      this.setAudioStream(stream);
    }
  }

  /**
   * Start the audio analysis loop
   */
  private startAnalysisLoop(): void {
    if (this.isAnalysisRunning) return;
    
    this.isAnalysisRunning = true;
    this.performAnalysis();
  }

  /**
   * Stop the audio analysis loop
   */
  private stopAnalysisLoop(): void {
    this.isAnalysisRunning = false;
    if (this.analysisLoopId !== null) {
      cancelAnimationFrame(this.analysisLoopId);
      this.analysisLoopId = null;
    }
  }

  /**
   * Perform audio analysis (called every frame)
   */
  private performAnalysis(): void {
    if (!this.isAnalysisRunning || !this.analyser || !this.frequencyData || !this.timeDomainData) {
      return;
    }
    
    // Get current audio data
    this.analyser.getByteFrequencyData(this.frequencyData);
    this.analyser.getByteTimeDomainData(this.timeDomainData);
    
    // Calculate volume metrics
    this.calculateVolumeMetrics();
    
    // Calculate frequency bands
    this.calculateFrequencyBands();
    
    // Send results to worker
    this.sendAnalysisResultsToWorker();
    
    // Schedule next analysis
    this.analysisLoopId = requestAnimationFrame(() => this.performAnalysis());
  }

  /**
   * Calculate RMS and peak volume from time domain data
   */
  private calculateVolumeMetrics(): void {
    if (!this.timeDomainData) return;
    
    let rmsSum = 0;
    let peak = 0;
    
    for (let i = 0; i < this.timeDomainData.length; i++) {
      // Convert from 0-255 to -1 to 1
      const sample = (this.timeDomainData[i] - 128) / 128;
      
      // RMS calculation
      rmsSum += sample * sample;
      
      // Peak calculation
      const absValue = Math.abs(sample);
      if (absValue > peak) {
        peak = absValue;
      }
    }
    
    // Calculate RMS (Root Mean Square)
    const rms = Math.sqrt(rmsSum / this.timeDomainData.length);
    
    // Update audio state (values between 0 and 1)
    this.audioState.volume.rms = rms;
    this.audioState.volume.peak = peak;
  }

  /**
   * Calculate frequency band values from frequency data
   */
  private calculateFrequencyBands(): void {
    if (!this.frequencyData || !this.audioContext) return;
    
    const nyquist = this.audioContext.sampleRate / 2;
    const binCount = this.frequencyData.length;
    
    // Define frequency bands (in Hz)
    const bands = {
      subBass: { min: 20, max: 60 },      // Sub-bass
      bass: { min: 60, max: 250 },        // Bass
      lowMid: { min: 250, max: 500 },     // Low midrange
      mid: { min: 500, max: 2000 },       // Midrange
      highMid: { min: 2000, max: 4000 },  // High midrange
      presence: { min: 4000, max: 6000 }, // Presence
      brilliance: { min: 6000, max: 20000 }, // Brilliance
      treble: { min: 2000, max: 20000 }   // Treble (combined high frequencies)
    };
    
    // Calculate band values
    for (const [bandName, range] of Object.entries(bands)) {
      const startBin = Math.floor((range.min / nyquist) * binCount);
      const endBin = Math.min(Math.floor((range.max / nyquist) * binCount), binCount - 1);
      
      let sum = 0;
      let count = 0;
      
      for (let i = startBin; i <= endBin; i++) {
        sum += this.frequencyData[i];
        count++;
      }
      
      // Average and normalize (0-255 to 0-1)
      const average = count > 0 ? sum / count : 0;
      (this.audioState.bands as any)[bandName] = average / 255;
    }
  }

  /**
   * Send analysis results to worker
   */
  private sendAnalysisResultsToWorker(): void {
    if (this.sendAnalysisResults) {
      // Include raw frequency data access
      const frequencyData = this.frequencyData ? new Uint8Array(this.frequencyData) : new Uint8Array(0);
      
      this.sendAnalysisResults({
        type: 'audio-analysis-update',
        data: {
          ...this.audioState,
          frequencyData, // For getFrequencyData() access
          timestamp: performance.now()
        }
      });
    }
  }

  /**
   * Reset audio values to defaults
   */
  private resetAudioValues(): void {
    this.audioState.volume.rms = 0;
    this.audioState.volume.peak = 0;
    
    for (const band in this.audioState.bands) {
      (this.audioState.bands as any)[band] = 0;
    }
  }

  /**
   * Reset all audio state (called when destroying)
   */
  public resetAudioState(): void {
    this.disconnectAudioStream();
    
    // Close AudioContext if it exists
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.resetAudioValues();
  }

  /**
   * Get current analysis configuration
   */
  public getAnalysisConfig(): { fftSize: number; smoothing: number } {
    return {
      fftSize: this.fftSize,
      smoothing: this.smoothingTimeConstant
    };
  }
} 